<script setup lang="ts">
    import { computed, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useForm } from '@volverjs/form-vue'
    import * as z from 'zod'

    const { t: $t } = useI18n({ useScope: 'global' })

    const props = defineProps<{
        part: unknown
        submitted?: boolean
        loading?: boolean
        error?: string
        readonly?: boolean
    }>()

    const toolPart = computed(() => {
        const part = props.part as {
            type: string
            toolCallId: string
            output?: {
                name: string
                email: string
                phone?: string
                metadata?: Record<string, unknown>
            }
        }
        return part
    })

    const output = computed(() => toolPart.value.output)

    const localSubmitted = useLocalStorage<boolean>(
        computed(
            () =>
                `${toolPart.value.toolCallId}-${toolPart.value.type}-submitted`,
        ),
        false,
    )

    const LeadFormSchema = z.object({
        name: z.string().min(1).max(255).default(''),
        email: z.email().max(255).default(''),
        phone: z
            .string()
            .max(50)
            .regex(/^[+0-9\s\-.()]*$/, $t('validation.invalidPhoneNumber'))
            .optional(),
    })

    const emit = defineEmits<{
        submit: [data: Record<string, unknown>]
    }>()

    const { VvForm, VvFormField, formData } = useForm(LeadFormSchema, {
        lazyLoad: true,
    })

    // Pre-populate form with output values
    watch(
        output,
        (newOutput) => {
            formData.value = {
                name: newOutput?.name || '',
                email: newOutput?.email || '',
                phone: newOutput?.phone || '',
            }
        },
        { immediate: true },
    )

    watch(
        () => props.submitted,
        (submitted) => {
            if (submitted) {
                localSubmitted.value = true
            }
        },
        { immediate: true },
    )

    const isSubmitted = computed(() => {
        return props.submitted || localSubmitted.value
    })

    const submitLead = async (data: Record<string, unknown>): Promise<void> => {
        emit('submit', { ...data, metadata: output.value?.metadata })
    }
</script>

<template>
    <div class="border border-surface-3 rounded-xl w-full overflow-hidden">
        <div
            class="px-sm py-6 bg-surface-1 text-12 border-b border-surface-3 text-word-3 flex items-center gap-8 min-h-40">
            <VvIcon name="ri:send-plane-2-line" class="text-16" />
            <strong class="font-bold">{{ $t('label.contactUs') }}</strong>
        </div>

        <Transition mode="out-in">
            <div v-if="!isSubmitted" class="p-sm">
                <VvForm v-model="formData" :readonly @submit="submitLead">
                    <VvFormField
                        name="name"
                        type="text"
                        :label="$t('label.name')"
                        :placeholder="$t('placeholder.name')"
                        icon="ri:user-line"
                        modifiers="compact no-label"
                        class="mb-md" />
                    <VvFormField
                        name="email"
                        type="email"
                        :label="$t('label.email')"
                        :placeholder="$t('placeholder.email')"
                        icon="ri:mail-line"
                        modifiers="compact no-label"
                        class="mb-md" />
                    <VvFormField
                        name="phone"
                        type="tel"
                        :label="$t('label.phone')"
                        :placeholder="$t('placeholder.phone')"
                        icon="ri:phone-line"
                        class="mb-md"
                        modifiers="compact no-label" />

                    <div class="flex justify-between items-center gap-16 mt-xs">
                        <div
                            class="text-smaller text-word-3 flex items-center gap-8">
                            <VvIcon
                                name="ri:shield-check-line"
                                class="text-16" />
                            <p>
                                {{ $t('message.leadFormInfo') }}
                                <span class="underline">{{
                                    $t('label.privacyPolicy')
                                }}</span>
                            </p>
                        </div>
                        <VvButton
                            type="submit"
                            modifiers="primary"
                            :loading
                            :disabled="loading || readonly"
                            icon-position="right"
                            class="shrink-0"
                            :label="$t('action.submit')" />
                    </div>
                </VvForm>
            </div>
            <div v-else class="p-sm">
                <div class="flex items-center gap-sm">
                    <VvIcon
                        name="ri:mail-check-line"
                        class="text-24 text-success" />
                    <div>
                        <strong class="font-semibold block text-success">
                            {{ $t('message.leadSubmittedTitle') }}
                        </strong>
                        <p class="text-word-3">
                            {{ $t('message.leadSubmittedMessage') }}
                        </p>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>
